import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Formik, Form } from 'formik';

import typeCheck from 'common/util/typeCheck';

import {
  SR_API_ACTION_ADMIN_SCRAPE_PARTNER_LISTINGS,
} from 'api/actionTypes';

import {
  SCREEN_ID_PARTNERS_LISTINGS_SCRAPER,
} from 'client/constants/screenIds';

import {
  createInitialValues,
  createFormValidationFn,
} from 'client/helpers/formikHelpers';

import log from 'client/lib/log';
import branch from 'client/core/branch';
import withApiState from 'client/helpers/withApiState';
import withTransitionHook from 'client/helpers/withTransitionHook';
import withScreenId from 'client/helpers/withScreenId';
import withScrollToTop from 'client/helpers/withScrollToTop';
import postWithState from 'client/actions/postWithState';
import getPartnerListAction from 'client/actions/Partner/getPartnerList';
import SrPage from 'client/components/SrPage';
import SrFormikField from 'client/components/SrFormikField';
import ButtonDeprecated from 'client/components/ButtonDeprecated';

function getPartnerListingsScraperFormFields({ partnersList = [] }) {
  typeCheck('partnersList::Array', partnersList);
  return [
    {
      id: 'overridePhotos',
      title: 'Override Photos',
      label: 'If this listing was already scraped, override the photos using Appfolio photos.',
      type: 'checkbox',
    },
    {
      id: 'partnerId',
      title: 'Partner',
      validations: ['required'],
      type: 'select',
      properties: {
        choices: partnersList.map(({ id, displayName }) => ({
          label: displayName,
          value: id,
        })),
      },
    },
  ];
}

@withTransitionHook
@withScreenId(SCREEN_ID_PARTNERS_LISTINGS_SCRAPER)
@withScrollToTop
@branch({
  partnersList: ['partners', 'list'],
})
@withApiState({
  scrapePartnerListingsApiState: {
    action: SR_API_ACTION_ADMIN_SCRAPE_PARTNER_LISTINGS,
  },
})
class AdminPartnerListingsScraperPage extends Component {
  static fetchData(state, inject) {
    return getPartnerListAction(inject);
  }

  static getPageMetadata = () => ({
    pageTitle: 'Scraper Partners Listings | Sunroom Rentals',
  })

  static propTypes = {
    partnersList: PropTypes.array,
    dispatch: PropTypes.func.isRequired,
    scrapePartnerListingsApiState: PropTypes.object.isRequired,
  }

  constructor(props) {
    super(props);
    this.state = {
      scrapeResult: null,
    };
  }

  _onSubmitScrapePartnersForm = (values) => {
    const { dispatch } = this.props;
    const partnerId = parseInt(values.partnerId, 10);
    typeCheck('partnerId::PositiveNumber', partnerId);
    if (
      values.overridePhotos
      && !global.confirm('Are you sure you want to override listing photos?')
    ) {
      return;
    }
    dispatch(postWithState, {
      action: SR_API_ACTION_ADMIN_SCRAPE_PARTNER_LISTINGS,
      payload: {
        partnerId: partnerId,
        overridePhotos: values.overridePhotos || false,
      },
    })
      .then((scrapeResult) => {
        this.setState({
          scrapeResult,
        });
      })
      .catch((error) => {
        log.error(error);
        this.setState({
          scrapeResult: null,
        });
      });
  }

  render() {
    const { partnersList, scrapePartnerListingsApiState } = this.props;
    const { scrapeResult } = this.state;
    const formFields = getPartnerListingsScraperFormFields({
      partnersList: partnersList || [],
    });
    return (
      <SrPage navbarProps={{ redirectLogoTo: '/admin' }}>
        <div className='width-90'>
          {!partnersList ? <div className='loading-gif' /> : (
            <Formik
              enableReinitialize
              initialValues={createInitialValues({ formFields })}
              validate={createFormValidationFn(formFields)}
              onSubmit={this._onSubmitScrapePartnersForm}
              render={(formikBag) => {
                return (
                  <Form>
                    {formFields.map((formField) => (
                      <SrFormikField
                        key={formField.id}
                        formField={formField}
                        formikBag={formikBag}
                      />
                    ))}
                    <ButtonDeprecated
                      type='submit'
                      disabled={!formikBag.isValid || scrapePartnerListingsApiState.inProgress}
                    >
                      {scrapePartnerListingsApiState.inProgress
                        ? 'scraping...'
                        : 'scrape'
                      }
                    </ButtonDeprecated>
                  </Form>
                );
              }}
            />
          )}
          {scrapePartnerListingsApiState.error && (
            <div className='formMessage error'>
              <span>{scrapePartnerListingsApiState.error.message}</span>
            </div>
          )}
          {scrapeResult && (
            <div>
              <pre>
                {JSON.stringify(scrapeResult, null, 2)}
              </pre>
            </div>
          )}
        </div>
      </SrPage>
    );
  }
}

AdminPartnerListingsScraperPage.displayName = 'AdminPartnerListingsScraperPage';

export default AdminPartnerListingsScraperPage;
